import type { QueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import {
  ARCHIVED_COUNT_QUERY_KEY,
  ARCHIVED_PROJECT_IDS_QUERY_KEY,
  PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY,
  readyMonthProjectIdsQueryKey,
} from '@/lib/projectEntityCache';
import {
  setActiveProjectsSnapshot,
  setArchivedProjectsSnapshot,
  setReadyMonthProjectsSnapshot,
} from '@/lib/projectListQueryCache';

/**
 * Запросы коллекций проектов. Ответ кладётся в entity-кэш (`['project', id]`),
 * сам запрос хранит только упорядоченные id — их патчит realtime-синхронизация.
 * Одни и те же опции у таблицы проектов и у /new/projects: общий кэш, без второго источника истины.
 */

export function activeProjectIdsQueryOptions(queryClient: QueryClient) {
  return {
    queryKey: PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY,
    queryFn: async (): Promise<string[]> => {
      const res = await api.projects.list({ excludeReady: true });
      const projects = Array.isArray(res) ? res : res.items;
      setActiveProjectsSnapshot(queryClient, projects);
      return queryClient.getQueryData<string[]>(PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY) ?? [];
    },
  };
}

export const READY_MONTH_SUMMARIES_QUERY_KEY = ['ready-month-summaries'] as const;

export function readyMonthSummariesQueryOptions() {
  return {
    queryKey: READY_MONTH_SUMMARIES_QUERY_KEY,
    queryFn: () => api.projects.readyMonths(),
  };
}

export function readyMonthProjectIdsQueryOptions(queryClient: QueryClient, year: number, month: number) {
  return {
    queryKey: readyMonthProjectIdsQueryKey(year, month),
    queryFn: async (): Promise<string[]> => {
      const projects = await api.projects.readyByMonth(year, month);
      setReadyMonthProjectsSnapshot(queryClient, { year, month }, projects);
      return queryClient.getQueryData<string[]>(readyMonthProjectIdsQueryKey(year, month)) ?? [];
    },
    /** Данные месяца подгружаются один раз; обновления — через сокет / merge кэша, не refetch при повторном раскрытии. */
    staleTime: Infinity,
  };
}

export function archivedCountQueryOptions() {
  return {
    queryKey: ARCHIVED_COUNT_QUERY_KEY,
    queryFn: async () => {
      const r = await api.projects.archivedCount();
      return r.count;
    },
  };
}

export function archivedProjectIdsQueryOptions(queryClient: QueryClient) {
  return {
    queryKey: ARCHIVED_PROJECT_IDS_QUERY_KEY,
    queryFn: async (): Promise<string[]> => {
      const projects = await api.projects.archivedList();
      setArchivedProjectsSnapshot(queryClient, projects);
      return queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY) ?? [];
    },
    staleTime: Infinity,
  };
}
